import React, { useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useGlobalContext } from '../../UserContext/UserContext';
import { getProfilePhotoUrl } from '../../services/SettingsService';
import { clearAuthStorage } from '../../services/sessionService';
import './UserProfileMenu.css';

function getInitials(name, email) {
    const source = (name || email || '').trim();
    if (!source) return '?';

    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return source.slice(0, 2).toUpperCase();
}

/**
 * props:
 * - variant: 'navbar' | 'sidebar'
 * - showDashboardLink: boolean
 * - showHomeLink: boolean
 */
function UserProfileMenu({
    variant = 'navbar',
    showDashboardLink = false,
    showHomeLink = false,
}) {
    const {
        profile,
        setUserToken,
        setShowLogin,
        refreshProfile,
    } = useGlobalContext();
    const history = useHistory();
    const rootRef = useRef(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!profile) {
            refreshProfile?.();
        }
    }, [profile, refreshProfile]);

    useEffect(() => {
        if (!open) return undefined;

        function handlePointerDown(event) {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                setOpen(false);
            }
        }

        function handleEscape(event) {
            if (event.key === 'Escape') setOpen(false);
        }

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [open]);

    if (!profile) {
        return (
            <div className={`user-profile user-profile--${variant} user-profile--loading`}>
                <div className="user-profile-avatar user-profile-avatar--placeholder" aria-hidden="true" />
                <div className="user-profile-text">
                    <span className="user-profile-name">Loading…</span>
                </div>
            </div>
        );
    }

    const displayName = (profile.name || '').trim() || profile.email || 'Account';
    const photoUrl = getProfilePhotoUrl(profile.profilePhoto);
    const initials = getInitials(profile.name, profile.email);

    function handleLogout() {
        clearAuthStorage();
        setUserToken(null);
        setShowLogin(true);
        setOpen(false);
        history.push('/');
    }

    return (
        <div
            ref={rootRef}
            className={`user-profile user-profile--${variant}${open ? ' is-open' : ''}`}
        >
            <button
                type="button"
                className="user-profile-trigger"
                aria-expanded={open}
                aria-haspopup="menu"
                onClick={() => setOpen((prev) => !prev)}
            >
                <span className="user-profile-avatar" aria-hidden="true">
                    {photoUrl ? (
                        <img src={photoUrl} alt="" className="user-profile-avatar-img" />
                    ) : (
                        <span className="user-profile-avatar-initials">{initials}</span>
                    )}
                </span>
                <span className="user-profile-text">
                    <span className="user-profile-name">{displayName}</span>
                    {profile.email ? (
                        <span className="user-profile-email">{profile.email}</span>
                    ) : null}
                </span>
                <Icon icon="mdi:chevron-down" className="user-profile-caret" aria-hidden="true" />
            </button>

            {open ? (
                <div className="user-profile-menu" role="menu" aria-label="Account menu">
                    <div className="user-profile-menu-header">
                        <span className="user-profile-menu-name">{displayName}</span>
                        {profile.email ? (
                            <span className="user-profile-menu-email">{profile.email}</span>
                        ) : null}
                    </div>

                    {showDashboardLink ? (
                        <Link
                            to="/dashboard"
                            className="user-profile-menu-item"
                            role="menuitem"
                            onClick={() => setOpen(false)}
                        >
                            <Icon icon="mdi:view-dashboard-outline" aria-hidden="true" />
                            Dashboard
                        </Link>
                    ) : null}

                    {showHomeLink ? (
                        <Link
                            to="/"
                            className="user-profile-menu-item"
                            role="menuitem"
                            onClick={() => setOpen(false)}
                        >
                            <Icon icon="mdi:home-outline" aria-hidden="true" />
                            Homepage
                        </Link>
                    ) : null}

                    <Link
                        to="/settings"
                        className="user-profile-menu-item"
                        role="menuitem"
                        onClick={() => setOpen(false)}
                    >
                        <Icon icon="mdi:account-cog-outline" aria-hidden="true" />
                        Profile &amp; settings
                    </Link>

                    <button
                        type="button"
                        className="user-profile-menu-item user-profile-menu-item--danger"
                        role="menuitem"
                        onClick={handleLogout}
                    >
                        <Icon icon="mdi:logout" aria-hidden="true" />
                        Logout
                    </button>
                </div>
            ) : null}
        </div>
    );
}

export default UserProfileMenu;
